import { useEffect, useState } from "react";
import { Box, Button, CircularProgress, TextField, Typography } from "@material-ui/core";
import useSecondsUntil from "./useSecondsUntil";
import { formatClock } from "./institutional-copy";
import { isPlausibleOtp, normalizeOtp } from "./institutional-email";
import { REPLAY_MASK } from "../../utils/replay-mask";

/**
 * The "enter the code we emailed you" step, shared by public sign-in and
 * signed-in linking. Purely presentational: the parent owns the challenge,
 * the API calls and the error code → copy mapping.
 *
 * Expiry is enforced client-side as UX only (verify is disabled and the
 * student is told to resend); the server rejects an expired challenge anyway.
 */
const InstitutionalOtpCodeStep = ({
  email,
  challenge,
  onVerify,
  onResend,
  onChangeEmail,
  busy = false,
  resending = false,
  errorMessage = null,
  verifyLabel = "Verify code",
  testIdPrefix = "institutional",
}) => {
  const [otp, setOtp] = useState("");
  const [inputError, setInputError] = useState(null);
  const expiresIn = useSecondsUntil(challenge?.expiresAt);
  const resendIn = useSecondsUntil(challenge?.resendAt);
  const expired = !!challenge && expiresIn <= 0;

  // A new challenge (resend) invalidates whatever was typed for the old one.
  useEffect(() => {
    setOtp("");
    setInputError(null);
  }, [challenge?.challengeId, challenge?.expiresAt]);

  const submit = (event) => {
    event.preventDefault();
    if (busy || expired) return;
    if (!isPlausibleOtp(otp)) {
      setInputError("Enter the numeric code from the email.");
      return;
    }
    setInputError(null);
    onVerify(normalizeOtp(otp));
  };

  return (
    <Box component="form" noValidate onSubmit={submit} className="institutional-otp-step" data-testid={`${testIdPrefix}-code-step`}>
      <Typography variant="body2" className="institutional-otp-sent-to">
        {/* data-hj-suppress: the typed address shown back (review B2). */}
        We sent a verification code to <strong {...REPLAY_MASK}>{email}</strong>. It can take a minute to arrive — check your spam or
        quarantine folder too.
      </Typography>
      <TextField
        id={`${testIdPrefix}-otp`}
        label="Verification code"
        value={otp}
        onChange={(e) => setOtp(e.target.value)}
        inputProps={{ inputMode: "numeric", autoComplete: "one-time-code", maxLength: 12, "data-testid": `${testIdPrefix}-otp-input` }}
        error={!!inputError}
        helperText={inputError || " "}
        disabled={busy || expired}
        fullWidth
        margin="normal"
        variant="outlined"
        autoFocus
      />
      {expired ? (
        <Typography variant="body2" role="status" className="institutional-otp-expired" data-testid={`${testIdPrefix}-otp-expired`}>
          This code has expired. Send a new code to continue.
        </Typography>
      ) : (
        <Typography variant="caption" className="institutional-otp-timer" data-testid={`${testIdPrefix}-otp-timer`}>
          Code expires in {formatClock(expiresIn)}
        </Typography>
      )}
      {errorMessage && (
        <Typography variant="body2" role="alert" className="institutional-error" data-testid={`${testIdPrefix}-error`} {...REPLAY_MASK}>
          {errorMessage}
        </Typography>
      )}
      <Box className="institutional-actions">
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={busy || expired}
          data-testid={`${testIdPrefix}-verify`}
        >
          {busy ? <CircularProgress size={20} color="inherit" /> : verifyLabel}
        </Button>
        <Button
          variant="text"
          color="primary"
          onClick={onResend}
          disabled={resending || busy || resendIn > 0}
          data-testid={`${testIdPrefix}-resend`}
        >
          {resendIn > 0 ? `Resend code (${formatClock(resendIn)})` : "Send a new code"}
        </Button>
        <Button variant="text" onClick={onChangeEmail} disabled={busy || resending} data-testid={`${testIdPrefix}-change-email`}>
          Use a different email
        </Button>
      </Box>
    </Box>
  );
};

export default InstitutionalOtpCodeStep;
